import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Leaf, Wind, Sun, ArrowUpRight, Globe2, BatteryCharging, Droplets } from 'lucide-react';

const RenewableEnergy: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "EcoTech Institute";
    
    return (
        <div className="min-h-screen bg-[#f8f9fa] font-sans text-slate-900 overflow-x-hidden selection:bg-[#00c853] selection:text-white">
            
            {/* Minimal Navigation */}
            <nav className="fixed w-full z-50 px-8 py-6">
                <div className="flex justify-between items-center backdrop-blur-md bg-white/70 rounded-full px-8 py-4 border border-white/50 shadow-sm max-w-7xl mx-auto">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#00c853] rounded-full flex items-center justify-center text-white">
                            <Leaf size={20} />
                        </div>
                        <span className="font-semibold text-lg tracking-tight">{schoolName}</span>
                    </div>
                    <div className="hidden md:flex gap-10 text-sm font-medium text-slate-600">
                        <a href="#" className="hover:text-[#00c853] transition-colors">Our Mission</a>
                        <a href="#" className="hover:text-[#00c853] transition-colors">Research Areas</a>
                        <a href="#" className="hover:text-[#00c853] transition-colors">Impact</a>
                    </div>
                    <Button className="rounded-full bg-[#1b4332] hover:bg-[#2d6a4f] text-white px-8">
                        Join Us
                    </Button>
                </div>
            </nav>

            {/* Hero Section - Swiss Grid Style */}
            <header className="pt-40 pb-20 px-4 md:px-8 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-y-12 md:gap-x-12">
                    
                    {/* Big Typography Headline */}
                    <div className="md:col-span-8">
                        <h1 className="text-6xl md:text-8xl font-semibold tracking-tighter leading-[0.95] mb-8 text-[#1b4332]">
                            Powering the <br/>
                            <span className="italic font-serif text-[#00c853]">Future</span> of Energy.
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-500 max-w-2xl leading-relaxed font-light">
                            We are an institute dedicated to the advancement of sustainable technologies. Engineering a cleaner, brighter tomorrow.
                        </p>
                    </div>

                    {/* Stats Column */}
                    <div className="md:col-span-4 flex flex-col justify-end gap-8 border-l border-slate-200 pl-8 md:pb-4">
                        <div>
                            <div className="text-5xl font-light text-[#1b4332] mb-2">100%</div>
                            <div className="text-sm font-bold uppercase tracking-widest text-slate-400">Carbon Neutral Campus</div>
                        </div>
                        <div>
                            <div className="text-5xl font-light text-[#1b4332] mb-2">$50M</div>
                            <div className="text-sm font-bold uppercase tracking-widest text-slate-400">Research Grants</div>
                        </div>
                    </div>
                </div>

                {/* Hero Image / Video Placeholder with Rounded Corners */}
                <div className="mt-16 relative w-full aspect-[21/9] bg-[#e9ecef] rounded-[3rem] overflow-hidden shadow-2xl">
                    <div className="absolute inset-0 bg-gradient-to-r from-[#1b4332]/80 to-transparent z-10"></div>
                     {/* Abstract organic shapes */}
                    <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-[#00c853] rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-pulse"></div>
                    
                    <div className="absolute bottom-12 left-12 md:left-20 z-20 text-white max-w-xl">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
                            <span className="w-2 h-2 rounded-full bg-[#00c853] animate-pulse"></span>
                            <span className="text-xs font-medium uppercase tracking-wider">Live System Data</span>
                        </div>
                        <h3 className="text-3xl font-light leading-tight">
                            "Innovation is not just about technology. It's about preserving our home."
                        </h3>
                    </div>
                </div>
            </header>

            {/* Scrolling Ticker (Simulated) */}
            <div className="border-y border-slate-200 bg-white py-4 overflow-hidden whitespace-nowrap">
                <div className="inline-flex items-center gap-16 animate-marquee text-slate-400 font-medium uppercase tracking-widest text-sm">
                    <span className="flex items-center gap-2"><Wind size={16} /> Solar Photovoltaics</span>
                    <span className="flex items-center gap-2"><Sun size={16} /> Wind Turbine Dynamics</span>
                    <span className="flex items-center gap-2"><BatteryCharging size={16} /> Solid State Batteries</span>
                    <span className="flex items-center gap-2"><Droplets size={16} /> Hydroelectric Systems</span>
                    <span className="flex items-center gap-2"><Globe2 size={16} /> Geo-Thermal Energy</span>
                    <span className="flex items-center gap-2"><Wind size={16} /> Solar Photovoltaics</span>
                    <span className="flex items-center gap-2"><Sun size={16} /> Wind Turbine Dynamics</span>
                </div>
            </div>

            {/* Programs Section */}
            <section className="py-24 px-4 md:px-8 max-w-7xl mx-auto">
                <div className="flex justify-between items-end mb-16">
                    <h2 className="text-4xl font-semibold text-[#1b4332]">Academic Tracks</h2>
                    <a href="#" className="flex items-center gap-2 text-[#00c853] font-medium hover:mr-2 transition-all">
                        View All Programs <ArrowUpRight size={18} />
                    </a>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { icon: Sun, title: "Solar Engineering", desc: "Next-gen photovoltaic cell design and implementation." },
                        { icon: Wind, title: "Wind Energy", desc: "Aerodynamics and large-scale turbine maintenance." },
                        { icon: BatteryCharging, title: "Energy Storage", desc: "Grid-scale battery systems and chemical engineering." },
                    ].map((item, i) => (
                        <div key={i} className="group bg-white p-10 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                            <div className="w-14 h-14 rounded-full bg-[#e8f5e9] flex items-center justify-center text-[#1b4332] mb-8 group-hover:scale-110 transition-transform">
                                <item.icon size={24} strokeWidth={1.5} />
                            </div>
                            <h3 className="text-2xl font-semibold text-[#1b4332] mb-4">{item.title}</h3>
                            <p className="text-slate-500 leading-relaxed mb-8">{item.desc}</p>
                            <div className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 group-hover:bg-[#00c853] group-hover:border-[#00c853] group-hover:text-white transition-all">
                                <ArrowUpRight size={16} />
                            </div>
                        </div>
                    ))}
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#1b4332] text-white py-20 px-8 rounded-t-[3rem] mx-2">
                 <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-12">
                    <div className="max-w-md">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-8 h-8 bg-[#00c853] rounded-full flex items-center justify-center text-white">
                                <Leaf size={16} />
                            </div>
                            <span className="font-semibold text-xl">{schoolName}</span>
                        </div>
                        <p className="text-white/60 leading-relaxed">
                            Developing the minds that will develop the world. Join us in the fight against climate change through innovation.
                        </p>
                    </div>
                    <div className="flex gap-16">
                        <div>
                            <h4 className="font-bold mb-6">Explore</h4>
                            <ul className="space-y-4 text-white/60">
                                <li><a href="#" className="hover:text-white">Admissions</a></li>
                                <li><a href="#" className="hover:text-white">Campus Map</a></li>
                                <li><a href="#" className="hover:text-white">Virtual Tour</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-bold mb-6">Contact</h4>
                            <ul className="space-y-4 text-white/60">
                                <li>info@ecotech.edu</li>
                                <li>+1 (555) 123-4567</li>
                            </ul>
                        </div>
                    </div>
                 </div>
                 <div className="max-w-7xl mx-auto mt-20 pt-8 border-t border-white/10 text-center md:text-left text-sm text-white/40">
                    &copy; 2026 {schoolName}. Design by Vochak.
                 </div>
            </footer>
        </div>
    );
};

export default RenewableEnergy;
